import { css } from '@emotion/react'
import { COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS } from '../../reducers/column'
import { SourcingRouteId } from '../../reducers/condition'
import { SourcingRoute } from '../../reducers/sourcingAnnotationCandidates'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'
import { useCallback, useRef, useState } from 'react'
import { SourcingRouteDropdown } from './SourcingRouteDropdown'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'

type Props = {
  candidates: SourcingRoute[]
  selected: SourcingRoute | undefined
  position?: {
    rowIndex: number
    rowCount: number
    rowHeight: number
    minTableHeight: number
  }
  onSelect: (id: SourcingRouteId, name: string) => void
  onDeselect: () => void
  onRequestEditModal: () => void
  disabled: boolean
}

const sourcingRouteWidth =
  COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS.sourcingRoute.width

export const SourcingRouteCell: React.FC<Props> = ({
  candidates,
  selected,
  position,
  onSelect,
  onDeselect,
  onRequestEditModal,
  disabled,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)

  const cellRef = useRef<HTMLTableCellElement>(null)
  useOnClickOutside([cellRef], () => {
    if (isOpenDropdown) {
      setIsOpenDropdown(!isOpenDropdown)
    }
  })

  const handleClick = useCallback(() => {
    if (disabled) return
    setIsOpenDropdown(!isOpenDropdown)
  }, [isOpenDropdown])

  const dropdownMaxHeight = 300
  const dropdownHeight = Math.min(
    candidates.length * 40 + 40,
    dropdownMaxHeight
  )
  let dropdownTop = undefined
  if (position) {
    const restRowsHeight =
      (position.rowCount - position.rowIndex) * position.rowHeight
    const restTableHeight =
      position.minTableHeight - (position.rowIndex + 1) * position.rowHeight
    const restHeight = Math.max(restRowsHeight, restTableHeight)
    if (restHeight < dropdownHeight) {
      dropdownTop = restHeight - dropdownHeight
    }
  }

  return (
    <td
      data-test-id="sourcingRoute"
      ref={cellRef}
      className={disabled ? 'disabled' : ''}
      css={css`
        min-width: ${sourcingRouteWidth}px;
        max-width: ${sourcingRouteWidth}px;
        &:hover {
          cursor: pointer;
          &::before {
            border-right: 1px solid rgb(217, 217, 217);
            border-left: 1px solid rgb(217, 217, 217);
          }
        }
        &::before {
          content: '';
          display: block;
          width: 100%;
          height: 100%;
          background-color: transparent;
          position: absolute;
          left: 0;
          top: 0;
          pointer-events: none;
        }
        &.disabled {
          cursor: not-allowed;
          opacity: 0.3;
        }
        position: relative;
      `}
      onClick={handleClick}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <div
          css={css`
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            word-break: break-all;
            white-space: break-spaces;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-height: 150%;
          `}
        >
          {selected?.name}
        </div>
        <IcomoonIcon
          css={css`
            font-size: 10px;
            display: block;
          `}
          name={'arrow1-down'}
        />
      </div>
      {isOpenDropdown ? (
        <SourcingRouteDropdown
          sourcingRoutes={candidates}
          selectedSourcingRouteId={selected?.id}
          maxHeight={dropdownMaxHeight}
          position={{ top: dropdownTop, left: 8 }}
          onSelectSourcingRoute={(id, name) => onSelect({ id }, name)}
          onDeselectSourcingRoute={() => onDeselect()}
          onRequestEditSourcingRouteModal={onRequestEditModal}
        />
      ) : null}
    </td>
  )
}
